/**
 * NextCaseHighlight コンポーネント
 *
 * 次に取り組むべき案件をハイライト表示
 * ワンタップステータス変更ボタン統合
 */

import React, { useMemo } from 'react';
import { Case } from '@/types';
import { TimeDisplay } from '../schedule/TimeDisplay';
import DriverCaseStatusButton from './DriverCaseStatusButton';

interface NextCaseHighlightProps {
  /** 案件一覧 */
  cases: Case[];
  /** ステータス更新時のコールバック */
  onStatusUpdated?: (caseId: string, newStatus: Case['status']) => void;
  /** エラー発生時のコールバック */
  onError?: (caseId: string, error: Error) => void;
  /** 追加クラス名 */
  className?: string;
}

/**
 * 次の案件を判定する関数
 * pickup_scheduled_atが最も近い未完了の案件を返す
 */
const getNextCase = (cases: Case[]): Case | null => {
  // 優先度1: 進行中の案件（picked_up / in_transit）
  const inProgress = cases
    .filter(c => c.status === 'picked_up' || c.status === 'in_transit')
    .sort(
      (a, b) =>
        new Date(`${a.pickupDate}T${a.pickupTimeStart}`).getTime() -
        new Date(`${b.pickupDate}T${b.pickupTimeStart}`).getTime()
    );

  if (inProgress.length > 0) {
    return inProgress[0];
  }

  // 優先度2: 未開始の案件（provisional, confirmed, assigned）で開始時刻が最も近い
  const upcoming = cases
    .filter(c => c.status === 'provisional' || c.status === 'confirmed' || c.status === 'assigned')
    .sort(
      (a, b) =>
        new Date(`${a.pickupDate}T${a.pickupTimeStart}`).getTime() -
        new Date(`${b.pickupDate}T${b.pickupTimeStart}`).getTime()
    );

  if (upcoming.length > 0) {
    return upcoming[0];
  }

  return null;
};

/**
 * 次の案件までの時間を計算（分単位）
 */
const getTimeUntilPickup = (pickupTime: string): number => {
  const now = new Date();
  const pickupDate = new Date(pickupTime);
  return Math.round((pickupDate.getTime() - now.getTime()) / (1000 * 60));
};

/**
 * 時間表示用の文字列を生成
 */
const formatTimeUntil = (minutes: number): string => {
  if (minutes < 0) {
    return 'その他';
  }

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours > 0) {
    return `あと${hours}時間${mins}分`;
  }

  if (minutes === 0) {
    return '直後';
  }

  return `あと${mins}分`;
};

/**
 * NextCaseHighlight コンポーネント
 *
 * 次に取り組むべき案件を大きくハイライト表示
 * ワンタップステータス変更ボタン統合
 */
export const NextCaseHighlight: React.FC<NextCaseHighlightProps> = ({
  cases,
  onStatusUpdated,
  onError,
  className = '',
}) => {
  const nextCase = useMemo(() => getNextCase(cases), [cases]);

  if (!nextCase) {
    return (
      <div
        className={`bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 rounded-lg shadow-md dark:shadow-lg p-6 border-2 border-dashed border-blue-300 dark:border-blue-600 ${className}`}
      >
        <div className='text-center'>
          <p className='text-gray-600 dark:text-gray-400 mb-2'>🎉</p>
          <p className='text-lg font-semibold text-gray-700 dark:text-gray-300'>
            本日の案件をすべて完了しました！
          </p>
          <p className='text-sm text-gray-500 dark:text-gray-400 mt-2'>お疲れ様でした</p>
        </div>
      </div>
    );
  }

  const minutesUntil = getTimeUntilPickup(`${nextCase.pickupDate}T${nextCase.pickupTimeStart}`);
  const timeDisplay = formatTimeUntil(minutesUntil);

  // ステータスに応じた背景色
  const isInProgress = nextCase.status === 'picked_up' || nextCase.status === 'in_transit';
  const bgGradient = isInProgress
    ? 'from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30'
    : 'from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30';
  const borderColor = isInProgress
    ? 'border-green-300 dark:border-green-600'
    : 'border-blue-300 dark:border-blue-600';
  const badgeBg = isInProgress
    ? 'bg-green-200 dark:bg-green-700 text-green-900 dark:text-green-200'
    : 'bg-blue-200 dark:bg-blue-700 text-blue-900 dark:text-blue-200';
  const badge = isInProgress ? '⭐ 進行中' : '⭐ 次の作業';

  return (
    <div
      className={`bg-gradient-to-br ${bgGradient} rounded-lg shadow-md dark:shadow-lg p-6 border-2 ${borderColor} ${className}`}
    >
      {/* ヘッダー */}
      <div className='flex items-center justify-between mb-4'>
        <h2 className='text-lg font-bold text-gray-900 dark:text-white'>{badge}</h2>
        <span className={`${badgeBg} px-3 py-1 rounded-full text-sm font-semibold`}>
          {timeDisplay}
        </span>
      </div>

      {/* 案件詳細 */}
      <div className='bg-white dark:bg-gray-800 rounded-lg p-4 mb-4 border border-gray-200 dark:border-gray-700'>
        {/* 引取地・配送地 */}
        <div className='flex items-start gap-2 mb-3'>
          <span className='text-2xl'>🚗</span>
          <div className='flex-1'>
            <p className='text-sm font-semibold text-gray-700 dark:text-gray-300'>
              {nextCase.pickupAddress} → {nextCase.deliveryAddress}
            </p>
            <p className='text-xs text-gray-500 dark:text-gray-400 mt-1'>
              {nextCase.vehicleType}（{nextCase.vehicleNumber}）
            </p>
          </div>
        </div>

        {/* 予定時刻 - TimeDisplay統合 */}
        <TimeDisplay
          pickupTimeStart={nextCase.pickupTimeStart}
          pickupTimeEnd={nextCase.pickupTimeEnd}
          deliveryTime={nextCase.deliveryTime}
          textSize='text-sm'
          spacing='space-y-1'
        />
        <div className='mb-3' />

        {/* 現在のステータス */}
        <div className='flex items-center gap-2'>
          <span className='text-xs font-medium text-gray-600 dark:text-gray-400'>ステータス:</span>
          <span className='px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded text-xs font-semibold'>
            {nextCase.status === 'picked_up'
              ? '🚗 引取済み'
              : nextCase.status === 'in_transit'
                ? '🛣️ 輸送中'
                : nextCase.status === 'assigned'
                  ? '✅ 配車済み'
                  : '📋 ' + nextCase.status}
          </span>
        </div>
      </div>

      {/* ステータス変更ボタン（ワンタップ対応） */}
      <div className='border-t border-gray-200 dark:border-gray-700 pt-4'>
        <DriverCaseStatusButton
          caseId={nextCase.id}
          currentStatus={nextCase.status}
          onStatusUpdated={status => onStatusUpdated?.(nextCase.id, status)}
          onError={error => onError?.(nextCase.id, error)}
          className='mb-2'
        />
      </div>
    </div>
  );
};

export default NextCaseHighlight;
